/**
 * The Nudge pieces a list row and a person chip compose (kanban 68cc2cfb):
 * the button beside a row, the confirm step under it, the note a refusal
 * or a per-person run leaves, the "about all N" button for one person, and
 * the "Nudged · <date>" chip a row shows. Shared by Triage and Routed away
 * so the two lists nudge the same way.
 */
import type { ReactNode } from 'react';
import { ThreadRefLabel } from './ThreadRefLabel';
import { nudgeConfirmText, nudgePersonConfirmText, nudgedLine, type NudgeTarget } from './nudgeView';
import { personNoteKey, type NudgeApi } from './useNudge';
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import styles from './QualityTriagePages.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

function first(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** One Nudge button per person the item waits on: "Nudge" when it is one
 *  person, "Nudge Laura" each when it is several. Disabled with the reason
 *  when the item cannot be nudged yet. */
export function NudgeButtons({
  targets,
  nudge,
  blocked = null,
}: {
  targets: NudgeTarget[];
  nudge: NudgeApi;
  blocked?: string | null;
}) {
  // A read-only viewer sends no nudges (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  if (targets.length === 0 || readOnly) return null;
  return (
    <>
      {targets.map((t) => {
        const open = nudge.pending?.thread.id === t.thread.id && nudge.pending.who === t.who;
        return (
          <button
            key={t.who}
            type="button"
            className={styles.rowAction}
            data-action="qcp_nudge_open"
            data-testid="nudge-button"
            onClick={() => nudge.open(t)}
            disabled={!!blocked || nudge.busy === t.thread.id || open}
            title={
              blocked ?? `Merlin sends ${t.who} a new message saying you asked and are still waiting`
            }
          >
            {targets.length > 1 ? `Nudge ${first(t.who)}` : 'Nudge'}
          </button>
        );
      })}
    </>
  );
}

/** The confirm step and the note under one row, when they are this row's. */
export function NudgeBelow({ thread, nudge }: { thread: DoorThreadRead; nudge: NudgeApi }) {
  const pending = nudge.pending?.thread.id === thread.id ? nudge.pending : null;
  const note = nudge.noteFor(thread.id);
  const busy = nudge.busy === thread.id;
  return (
    <>
      {pending && (
        <div
          className={styles.rowConfirm}
          role="group"
          aria-label={`Nudge ${pending.who} about ${thread.thread_no}?`}
          data-testid="nudge-confirm"
        >
          <span className={styles.rowConfirmText}>
            <ThreadRefLabel thread={thread} /> — {nudgeConfirmText(pending)}
          </span>
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => void nudge.send(pending)}
            disabled={busy}
            data-action="qcp_nudge_send"
            data-testid="nudge-send"
          >
            {busy ? 'Sending…' : 'Send the nudge'}
          </button>
          <button
            type="button"
            className={styles.rowConfirmNo}
            onClick={nudge.cancel}
            disabled={busy}
            data-action="qcp_nudge_cancel"
          >
            Not now
          </button>
        </div>
      )}
      {note && (
        <p className={note.tone === 'error' ? styles.errorNote : styles.footNote} role={note.tone === 'error' ? 'alert' : 'status'} data-testid="nudge-note">
          {note.message}
        </p>
      )}
    </>
  );
}

/** A row that offers Nudge: the row itself, the buttons beside it, and the
 *  confirm step or note under it. For rows that are a bare button (Triage);
 *  Routed away composes the pieces into its own row group. */
export function NudgeRow({
  thread,
  targets,
  nudge,
  blocked = null,
  children,
}: {
  thread: DoorThreadRead;
  targets: NudgeTarget[];
  nudge: NudgeApi;
  blocked?: string | null;
  children: ReactNode;
}) {
  if (targets.length === 0) return <>{children}</>;
  return (
    <div className={styles.rowGroup}>
      <div className={styles.rowWrap}>
        {children}
        <NudgeButtons targets={targets} nudge={nudge} blocked={blocked} />
      </div>
      <NudgeBelow thread={thread} nudge={nudge} />
    </div>
  );
}

/** "Nudge Laura about all 3" for the person the list is narrowed to, with
 *  its confirm step and the summary it leaves. */
export function NudgePerson({
  who,
  targets,
  nudge,
}: {
  who: string;
  targets: NudgeTarget[];
  nudge: NudgeApi;
}) {
  const readOnly = useQualityReadOnly();
  const key = personNoteKey(who);
  const pending = nudge.personPending && nudge.personPending.who === who ? nudge.personPending : null;
  const note = nudge.noteFor(key);
  const busy = nudge.busy === key;
  const count = targets.length;
  if (readOnly) return null;
  return (
    <div className={styles.nudgePerson} data-testid="nudge-person">
      {count > 0 && !pending && (
        <button
          type="button"
          className={styles.rowAction}
          data-action="qcp_nudge_person_open"
          data-testid="nudge-person-button"
          onClick={() => nudge.openPerson(who, targets)}
          disabled={busy || !!nudge.busy}
        >
          {busy ? 'Nudging…' : `Nudge ${first(who)} about ${count === 1 ? 'this item' : `all ${count}`}`}
        </button>
      )}
      {pending && (
        <div className={styles.rowConfirm} role="group" aria-label={`Nudge ${who} about every item?`} data-testid="nudge-person-confirm">
          <span className={styles.rowConfirmText}>{nudgePersonConfirmText(who, pending.targets.length)}</span>
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => void nudge.sendPerson()}
            disabled={busy}
            data-action="qcp_nudge_person_send"
            data-testid="nudge-person-send"
          >
            {busy ? 'Sending…' : 'Send the nudges'}
          </button>
          <button type="button" className={styles.rowConfirmNo} onClick={nudge.cancel} disabled={busy} data-action="qcp_nudge_cancel">
            Not now
          </button>
        </div>
      )}
      {note && (
        <p className={note.tone === 'error' ? styles.errorNote : styles.footNote} role={note.tone === 'error' ? 'alert' : 'status'} data-testid="nudge-person-note">
          {note.message}
        </p>
      )}
    </div>
  );
}

/** "↪ Nudged Laura Caswell · Oct 21" on a row; nothing when never nudged. */
export function NudgedChip({ thread }: { thread: Pick<DoorThreadRead, 'last_nudge'> }) {
  const line = nudgedLine(thread);
  return line ? (
    <span className={`${styles.chip} ${styles.chipNudged}`} data-testid="row-nudged">
      ↪ {line}
    </span>
  ) : null;
}
